/**
 * NotificationContext — presents an empty notification state.
 *
 * Every deployed dashboard runs with the notification service off, so the provider
 * makes no request, opens no stream and holds no notifications. The context shape
 * stays, so the bell, the centre and the toast host keep rendering their empty states.
 */

import React, { createContext, useContext } from 'react';
import type {
  Notification,
  NotificationCategory,
  NotificationPreferences,
} from '../../api/notifications.js';

interface NotificationState {
  notifications: Notification[];
  unreadCounts: Partial<Record<NotificationCategory, number>>;
  totalUnread: number;
  total: number;
  isLoading: boolean;
  error: string | null;
  preferences: NotificationPreferences | null;
}

interface NotificationActions {
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: (category?: NotificationCategory) => Promise<void>;
  archive: (id: string) => Promise<void>;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  updatePreferences: (prefs: Partial<NotificationPreferences>) => Promise<void>;
}

export interface NotificationContextValue extends NotificationState, NotificationActions {
  /** New notifications arrived via SSE since last view */
  newArrivals: number;
  clearNewArrivals: () => void;
  /** Toast queue for high-priority notifications */
  toasts: Notification[];
  dismissToast: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export function useNotifications(): NotificationContextValue {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return ctx;
}

interface NotificationProviderProps {
  children: React.ReactNode;
  /** JWT access token (unused: the provider makes no call) */
  accessToken?: string;
  /** Current active category filter (unused: there is nothing to filter) */
  activeCategory?: NotificationCategory | 'all';
}

const noop = async (): Promise<void> => undefined;
const noopSync = (): void => undefined;

const EMPTY_VALUE: NotificationContextValue = {
  notifications: [],
  unreadCounts: {},
  totalUnread: 0,
  total: 0,
  isLoading: false,
  error: null,
  preferences: null,
  markAsRead: noop,
  markAllAsRead: noop,
  archive: noop,
  loadMore: noop,
  refresh: noop,
  updatePreferences: noop,
  toasts: [],
  dismissToast: noopSync,
  newArrivals: 0,
  clearNewArrivals: noopSync,
};

export function NotificationProvider({ children }: NotificationProviderProps) {
  return (
    <NotificationContext.Provider value={EMPTY_VALUE}>
      {children}
    </NotificationContext.Provider>
  );
}
